/**
 * MessagePlayerDialog - thin Dialog wrapper around `ComposeMailForm`, pre-addressed
 * to a character from the character-card "Message the player" quick action (#2160).
 * This is an OOC affordance - it messages whoever currently plays the character, not
 * the character in-fiction (see `AGENT_GLOSSARY.md` in `world/roster`, ADR-0226).
 *
 * Externally controlled (`open`/`onClose`), matching `JournalComposerDialog`'s
 * pattern rather than owning its own trigger - `CharacterCardDrawer` opens it
 * from its own quick-action button.
 *
 * `recipientTenureId`/`recipientDisplay` come from the card's resolved live
 * tenure (`entry.tenures.find(t => t.end_date === null)`) - the card only
 * offers this action when a live tenure exists (a vacant character has no one
 * to address), so both are always required here. `senderTenureId` is optional:
 * only pass it when the viewer's own current tenure is unambiguous from
 * context; otherwise omit it and `ComposeMailForm` falls back to
 * `MyTenureSelect` so the player picks among their own tenures.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  NO_DESCRIPTION,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ComposeMailForm } from '@/mail/components/ComposeMailForm';

interface MessagePlayerDialogProps {
  open: boolean;
  onClose: () => void;
  /** The pre-addressed recipient's live `RosterTenure` id. */
  recipientTenureId: number;
  /** The pre-addressed recipient's display name, shown as "To: {name}". */
  recipientDisplay: string;
  /** The viewer's own tenure id, when unambiguous - hides `MyTenureSelect`. */
  senderTenureId?: number;
}

export function MessagePlayerDialog({
  open,
  onClose,
  recipientTenureId,
  recipientDisplay,
  senderTenureId,
}: MessagePlayerDialogProps) {
  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent {...NO_DESCRIPTION}>
        <DialogHeader>
          <DialogTitle>Message the player of {recipientDisplay}</DialogTitle>
        </DialogHeader>
        <ComposeMailForm
          initialRecipientTenureId={recipientTenureId}
          initialRecipientDisplay={recipientDisplay}
          fixedSenderTenureId={senderTenureId}
          onSent={() => {
            toast.success(`Message sent to ${recipientDisplay}'s player.`);
            onClose();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
